// Assets that may be uploaded per tenant under "{subdomain}/assets" in S3.
// The values are the object paths relative to the tenant folder.
export const TENANT_LOGIN_ASSETS = {
   logo: 'assets/login_logo.png',
   photo: 'assets/login_photo.jpg',
};

// Bundled images used whenever the tenant has not uploaded its own asset.
export const LOCAL_LOGIN_ASSETS = {
   logo: '/images/logo.png',
   photo: '/images/logo_default.jpg',
};

// sessionStorage key prefix used to remember assets the tenant does not have.
// A tenant without an uploaded asset makes S3 answer with 403, so we record the
// miss for the rest of the browser session and skip the doomed request.
const MISSING_ASSET_KEY_PREFIX = 'lms-missing-tenant-asset:';

const getTenantSubdomain = () => localStorage.getItem('lms-subdomain');

const buildTenantAssetUrl = (subdomain, assetPath) =>
   `${process.env.REACT_APP_API_S3_URL}/${subdomain}/${assetPath}`;

const isTenantAssetKnownMissing = (subdomain, assetPath) => {
   try {
      return sessionStorage.getItem(`${MISSING_ASSET_KEY_PREFIX}${subdomain}:${assetPath}`) === '1';
   } catch (error) {
      // Private browsing / disabled storage: behave as if nothing is cached.
      return false;
   }
};

/**
 * Remembers that the given tenant asset is missing so later mounts fall back to
 * the bundled image immediately instead of requesting a missing object again.
 */
export const markTenantAssetMissing = (assetPath) => {
   const subdomain = getTenantSubdomain();

   if (!subdomain || !assetPath) {
      return;
   }

   try {
      sessionStorage.setItem(`${MISSING_ASSET_KEY_PREFIX}${subdomain}:${assetPath}`, '1');
   } catch (error) {
      // Storage unavailable: nothing to cache, the onError fallback still runs.
   }
};

/**
 * Resolves a tenant asset to its S3 URL synchronously.
 *
 * The tenant branding URL is derived from the subdomain alone (no existence
 * round-trip), so it can be used as the initial value during the very first
 * render. If the object turns out to be missing the caller's <img onError>
 * handler falls back to the bundled image and records the miss with
 * {@link markTenantAssetMissing}.
 */
export const resolveTenantAssetSync = (assetPath, fallback) => {
   const subdomain = getTenantSubdomain();

   if (!subdomain || !assetPath) {
      return fallback;
   }

   if (isTenantAssetKnownMissing(subdomain, assetPath)) {
      return fallback;
   }

   return buildTenantAssetUrl(subdomain, assetPath);
};

/**
 * Resolves the login page branding (logo + hero photo) synchronously.
 */
export const resolveLoginAssetsSync = () => ({
   logo: resolveTenantAssetSync(TENANT_LOGIN_ASSETS.logo, LOCAL_LOGIN_ASSETS.logo),
   photo: resolveTenantAssetSync(TENANT_LOGIN_ASSETS.photo, LOCAL_LOGIN_ASSETS.photo),
});
